// Client timeline (Timeline & Facts spec): one chronological stream merging the
// DERIVED layer (documents, quiet — they are already the record) with the CURATED layer
// (events, prominent).
//
// One-man-show discipline: the derived layer is complete with zero curation; everything
// curated here is optional, editable, deletable. Agent-created rows carry a quiet marker
// and a one-click confirm/delete; document supersession chains collapse onto their current
// head as a compact v-chain row.
//
// The "What we currently know" facts panel used to live at the top of this component; it
// now lives in ClientFactsPanel, pinned to the client page's right rail so it stays in
// view on every tab. Reconstruction still lands facts — onFactsChanged tells the rail.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Banknote, BookOpen, File, FileSignature, FileText,
  Image as ImageIcon, Mail, Package, Pencil, Presentation, Sparkles, StickyNote,
  Trash2, Users,
} from 'lucide-react';

import { useToast } from './Toast.js';
import { useT, useLocale } from '../i18n/index.js';
import { Tx } from './Tx.js';
import './ClientTimeline.de.js';
import './ClientTimeline.fr.js';
import ConfirmModal from './ConfirmModal.js';
import DischargeModal from './DischargeModal.js';
import { agentApi } from '../api/agent.js';
import { documentsApi, type LibraryDocumentRow } from '../api/documents.js';
import type { ClientDetail } from '../api/crm.js';
import {
  EVENT_KINDS, timelineApi,
  type DocumentEntry, type EventEntry, type EventKind,
  type Participant, type TimelineEntry, type TimelineResponse,
} from '../api/timeline.js';

const FOLDER_ICONS: Record<string, typeof File> = {
  correspondence: Mail,
  meeting: Users,
  agreement: FileSignature,
  financial: Banknote,
  specification: FileText,
  presentation: Presentation,
  image: ImageIcon,
  note: StickyNote,
  deliverable: Package,
  reference: BookOpen,
};

interface Props {
  detail: ClientDetail;
  /** A reconstruction pass also records facts — the page bumps the facts rail on this. */
  onFactsChanged?: () => void;
  /** A commitment was discharged here — the brief's BALL cell is downstream of it. */
  onCommitmentChanged?: () => void;
  /** Scroll to this event ref on arrival — the rail's BALL cell routes to a commitment. */
  anchorRef?: string | null;
}

// ── event editor draft ────────────────────────────────────────────────────────

interface EventDraft {
  id: number | null; // null = create
  kind: EventKind;
  occurred_at: string;
  occurred_time: string;
  title: string;
  summary: string;
  engagement_id: string; // '' = none
  participantsText: string; // one per line: Name <email>
  document_ids: number[];
}

const emptyEventDraft = (): EventDraft => ({
  id: null, kind: 'meeting', occurred_at: '', occurred_time: '', title: '', summary: '',
  engagement_id: '', participantsText: '', document_ids: [],
});

/** "Name <email>" lines → participants; matching to contact_id is deliberately not built. */
function parseParticipants(text: string): Participant[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const m = line.match(/^(.*?)\s*<([^>]+)>\s*$/);
      if (m && m[1].trim()) return { name: m[1].trim(), email: m[2].trim() };
      return { name: line };
    });
}

const participantsToText = (list: Participant[]): string =>
  (list ?? []).map((p) => (p.email ? `${p.name} <${p.email}>` : p.name)).join('\n');

export default function ClientTimeline({ detail, onFactsChanged, onCommitmentChanged, anchorRef }: Props) {
  const toast = useToast();
  const t = useT();
  const { tp, fmtDate } = useLocale();
  const clientId = detail.id;

  const [stream, setStream] = useState<TimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters: engagement + date range are server-side; folder/kind chips are client-side
  // and each act only on their own layer (a folder pick never hides events, and vice versa).
  const [engFilter, setEngFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [folderFilter, setFolderFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');

  const [eventDraft, setEventDraft] = useState<EventDraft | null>(null);
  const [eventSaving, setEventSaving] = useState(false);

  const [reconBusy, setReconBusy] = useState(false);
  const [confirm, setConfirm] = useState<{ id: number; label: string } | null>(null);
  const [busy, setBusy] = useState(false);

  /** The commitment being discharged — the "promise was kept" write, with its evidence. */
  const [discharging, setDischarging] = useState<EventEntry | null>(null);

  /** Whole-library rows for the event anchor picker — loaded lazily. */
  const [libraryDocs, setLibraryDocs] = useState<LibraryDocumentRow[] | null>(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const loadStream = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await timelineApi.stream(clientId, {
        engagement_id: engFilter ? Number(engFilter) : undefined,
        from: from || undefined,
        to: to || undefined,
      });
      if (mounted.current) setStream(res);
    } catch (err) {
      if (!silent) toast('err', (err as Error).message);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [clientId, engFilter, from, to, toast]);

  useEffect(() => { void loadStream(); }, [loadStream]);

  // The rail's BALL cell routes to ONE commitment; land on it rather than on the top of a
  // stream the operator then has to search. Fires once per arrival, after the rows exist.
  useEffect(() => {
    if (!anchorRef || !stream) return;
    const node = document.getElementById(`tl-${anchorRef}`);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [anchorRef, stream]);

  const ensureLibrary = useCallback(async () => {
    if (libraryDocs) return libraryDocs;
    try {
      const res = await documentsApi.library(clientId);
      if (mounted.current) setLibraryDocs(res.rows);
      return res.rows;
    } catch (err) {
      toast('err', (err as Error).message);
      return [];
    }
  }, [clientId, libraryDocs, toast]);

  // ── stream shaping: filters + v-chain collapse ───────────────────────────────

  const shaped = useMemo(() => {
    const entries = stream?.entries ?? [];
    const docs = entries.filter((e): e is DocumentEntry => e.layer === 'document');
    const byRef = new Map(docs.map((d) => [d.ref, d]));

    // A superseded document whose SUCCESSOR is also in the stream collapses onto it —
    // the lineage renders once, on the current head, as a compact v-chain row.
    const absorbed = new Set<string>();
    for (const d of docs) {
      if (d.superseded_by_ref && byRef.has(d.superseded_by_ref)) absorbed.add(d.ref);
    }
    const chainOf = (head: DocumentEntry): DocumentEntry[] => {
      const chain = [head];
      const seen = new Set([head.ref]);
      let cur = head;
      while (cur.supersedes_ref && byRef.has(cur.supersedes_ref) && !seen.has(cur.supersedes_ref)) {
        cur = byRef.get(cur.supersedes_ref)!;
        seen.add(cur.ref);
        chain.unshift(cur);
      }
      return chain;
    };

    const folders = [...new Set(docs.map((d) => d.folder ?? 'unclassified'))].sort();
    const kinds = [...new Set(entries.filter((e) => e.layer === 'event').map((e) => (e as EventEntry).kind))]
      .sort((a, b) => EVENT_KINDS.indexOf(a) - EVENT_KINDS.indexOf(b));

    const visible = entries.filter((e) => {
      if (e.layer === 'document') {
        if (absorbed.has((e as DocumentEntry).ref)) return false;
        if (folderFilter && ((e as DocumentEntry).folder ?? 'unclassified') !== folderFilter) return false;
      } else if (kindFilter && (e as EventEntry).kind !== kindFilter) return false;
      return true;
    });

    return { visible, chainOf, folders, kinds };
  }, [stream, folderFilter, kindFilter]);

  // ── reconstruction pass (librarian-button discipline: busy while it runs,
  //    refresh as rows land, capped and advisory — a blip must not strand the button) ──

  async function runReconstruct() {
    setReconBusy(true);
    try {
      const run = await timelineApi.reconstruct(clientId, engFilter ? Number(engFilter) : undefined);
      toast('ok', t('Reconstruction started — the agent is reading the library (run #{id}).', { id: run.id }));
      const deadline = Date.now() + 30 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        if (!mounted.current) return;
        let status = '';
        let stats: Record<string, number> = {};
        try {
          const r = await agentApi.getRun(run.id);
          status = r.status;
          stats = (r.stats ?? {}) as Record<string, number>;
        } catch { break; }
        await loadStream(true);
        onFactsChanged?.();
        if (['done', 'error', 'aborted', 'timeout'].includes(status)) {
          const events = stats.timeline_events ?? 0;
          const recorded = stats.facts ?? 0;
          if (status === 'done') {
            toast('ok', t('Reconstruction finished — {events}, {facts} recorded. Every row is yours to edit or delete.', { events: tp(events, '{n} event', '{n} events'), facts: tp(recorded, '{n} fact', '{n} facts') }));
          } else {
            toast('warn', events || recorded
              ? t('Reconstruction {status} — {events} event(s), {facts} fact(s) landed before it stopped.', { status: t(status), events, facts: recorded })
              : t('Reconstruction {status}.', { status: t(status) }));
          }
          break;
        }
      }
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      if (mounted.current) setReconBusy(false);
    }
  }

  // ── event mutations ──────────────────────────────────────────────────────────

  async function openEventEditor(ev?: EventEntry) {
    void ensureLibrary();
    if (!ev) { setEventDraft(emptyEventDraft()); return; }
    setEventDraft({
      id: ev.id,
      kind: ev.kind,
      occurred_at: (ev.occurred_at ?? '').slice(0, 10),
      occurred_time: ev.occurred_time ? String(ev.occurred_time).slice(0, 5) : '',
      title: ev.title,
      summary: ev.summary ?? '',
      engagement_id: ev.engagement_id ? String(ev.engagement_id) : '',
      participantsText: participantsToText(ev.participants),
      document_ids: (ev.documents ?? []).map((d) => d.id),
    });
  }

  async function saveEvent() {
    if (!eventDraft) return;
    if (!eventDraft.title.trim()) { toast('warn', t('The event needs a title.')); return; }
    if (!eventDraft.occurred_at) { toast('warn', t('When did it happen? occurred-at is the date per the source, never today by default.')); return; }
    setEventSaving(true);
    try {
      const body = {
        kind: eventDraft.kind,
        occurred_at: eventDraft.occurred_at,
        occurred_time: eventDraft.occurred_time || null,
        title: eventDraft.title.trim(),
        summary: eventDraft.summary.trim() || null,
        engagement_id: eventDraft.engagement_id ? Number(eventDraft.engagement_id) : null,
        participants: parseParticipants(eventDraft.participantsText),
        document_ids: eventDraft.document_ids,
      };
      if (eventDraft.id) {
        await timelineApi.updateEvent(eventDraft.id, body);
        toast('ok', t('Event updated'));
      } else {
        const created = await timelineApi.createEvent({ client_id: clientId, ...body });
        toast('ok', t('{ref} recorded', { ref: created.ref }));
      }
      setEventDraft(null);
      await loadStream(true);
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      setEventSaving(false);
    }
  }

  async function runConfirmModal() {
    if (!confirm) return;
    setBusy(true);
    try {
      const out = await timelineApi.deleteEvent(confirm.id);
      toast('ok', t('{ref} deleted', { ref: out.ref }));
      await loadStream(true);
      setConfirm(null);
    } catch (err) {
      toast('err', (err as Error).message);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  // ── stream rendering ─────────────────────────────────────────────────────────

  function renderDocRow(d: DocumentEntry, prevDate: string | null) {
    const Icon = FOLDER_ICONS[d.folder ?? ''] ?? File;
    const chain = shaped.chainOf(d);
    return (
      <div key={`d${d.id}`} className="tl-row tl-row--doc">
        <div className="tl-date">{d.date && d.date !== prevDate ? fmtDate(d.date) : ''}</div>
        <div className="tl-body">
          <span className="mono" style={{ color: 'var(--ink-4)' }}>{d.ref}</span>
          {' '}
          <Icon className="icon-sm icon-inline" style={{ width: 12, height: 12, color: 'var(--ink-4)' }} />
          {' '}
          <span className="tl-doc-name" style={{ overflowWrap: 'anywhere' }}>{d.original_filename}</span>
          {d.authored_by && <span> · {d.authored_by}</span>}
          {d.status === 'superseded' && d.superseded_by_ref && (
            <span className="mono" style={{ color: 'var(--ink-4)' }}> · {t('superseded')} → {d.superseded_by_ref}</span>
          )}
          {d.ai_summary && <div style={{ color: 'var(--ink-4)' }}>{d.ai_summary}</div>}
          {chain.length > 1 && (
            <div className="tl-chain" title={t('Supersession lineage — explicit links and dates, never filename version labels')}>
              {chain.map((c, i) => (
                <span key={c.ref}>
                  {i > 0 && ' → '}
                  {c.ref}{c.date ? ` (${fmtDate(c.date)})` : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderEventRow(ev: EventEntry, prevDate: string | null) {
    const isCommitment = ev.kind === 'commitment';
    const open = isCommitment && !ev.discharged_at;
    return (
      <div key={`e${ev.id}`} id={`tl-${ev.ref}`} className="tl-row tl-row--event" style={{ scrollMarginTop: 'calc(var(--topbar-h, 48px) + 24px)' }}>
        <div className="tl-date">
          {ev.date && ev.date !== prevDate ? fmtDate(ev.date) : ''}
          {ev.time && <div>{String(ev.time).slice(0, 5)}</div>}
        </div>
        <div className="tl-body">
          <div className="flex items-center gap-8 flex-wrap">
            <span className="badge badge-plain">{t(ev.kind)}</span>
            <Tx type="timeline_event" id={ev.id} field="title" original={ev.title}
                engagementId={ev.engagement_id} className="font-medium text-sm text-ink-1"
                style={{ overflowWrap: 'anywhere' }} as="span" />
            {/* A commitment is ball-attributable from birth, and says whether it was kept. */}
            {isCommitment && (
              open
                ? (
                  <span className="badge badge-warn">
                    {ev.commitment_owner ? t('open · with {owner}', { owner: ev.commitment_owner }) : t('open · owner unrecorded')}
                  </span>
                )
                : <span className="badge badge-ok">{t('discharged {date}', { date: fmtDate(ev.discharged_at) })}</span>
            )}
            <span className="ml-auto" />
            {open && (
              <button
                className="btn btn-sm"
                title={t('Record that the promise was kept — a date and the artefact that kept it')}
                onClick={() => { void ensureLibrary(); setDischarging(ev); }}
              >
                {t('Discharge')}
              </button>
            )}
            <span className="mono text-xs" style={{ color: 'var(--ink-4)' }}>{ev.ref}</span>
            <button className="btn btn-sm btn-ghost" title={t('Edit event')} onClick={() => void openEventEditor(ev)}>
              <Pencil className="icon-sm" style={{ width: 12, height: 12 }} />
            </button>
            <button
              className="btn btn-sm btn-ghost" title={t('Delete event')}
              onClick={() => setConfirm({ id: ev.id, label: `${ev.ref} — ${ev.title}` })}
            >
              <Trash2 className="icon-sm" style={{ width: 12, height: 12 }} />
            </button>
          </div>
          {ev.summary && (
            <Tx type="timeline_event" id={ev.id} field="summary" original={ev.summary}
                engagementId={ev.engagement_id} className="text-sm text-ink-2 mt-4"
                style={{ lineHeight: 1.5 }} as="div" />
          )}
          <div className="flex items-center gap-8 flex-wrap mt-4 text-xs text-ink-3">
            {ev.participants?.length > 0 && (
              <span title={ev.participants.map((p) => p.email ? `${p.name} <${p.email}>` : p.name).join(', ')}>
                <Users className="icon-sm icon-inline" style={{ width: 12, height: 12 }} />{' '}
                {ev.participants.map((p) => p.name).join(', ')}
              </span>
            )}
            {ev.engagement_code && <span className="badge badge-plain mono">{ev.engagement_code}</span>}
            {(ev.documents ?? []).map((d) => (
              <span key={d.id} className="badge badge-plain mono" title={d.original_filename}>{d.ref}</span>
            ))}
            {(ev.documents ?? []).length === 0 && (
              <span className="badge badge-plain" title={t('No source document anchors this event — fine, but anchored is better')}>
                {t('unanchored')}
              </span>
            )}
            {ev.created_by_kind === 'agent' && (
              <span className="badge badge-plain" title={t('Recorded by the reconstruction agent — always yours to correct')}>
                <Sparkles className="icon-sm" style={{ width: 10, height: 10 }} /> {t('agent')}
              </span>
            )}
            {/* A discharge carries its evidence, always — that is what makes it a record. */}
            {ev.discharged_at && (ev.discharged_by_document_ref || ev.discharged_by_event_ref) && (
              <span>
                {t('kept by')} <span className="mono">{ev.discharged_by_document_ref ?? ev.discharged_by_event_ref}</span>
                {ev.discharge_note ? ` — ${ev.discharge_note}` : ''}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  const activeEngagements = detail.engagements.filter((e) => !e.archived_at);

  // ── layout ───────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Actions */}
      <div className="flex items-center gap-8 mb-16">
        <div className="text-xs text-ink-3">
          {stream && (
            <span>
              <span className="mono">{stream.documents}</span> {tp(stream.documents, 'document', 'documents')} ·{' '}
              <span className="mono">{stream.events}</span> {tp(stream.events, 'event', 'events')}
            </span>
          )}
        </div>
        <span className="ml-auto" />
        <button className="btn btn-sm" onClick={() => void openEventEditor()}>{t('Add event')}</button>
        <button
          className="btn btn-sm"
          disabled={reconBusy || !stream?.documents}
          title={t('Agent pass over the whole library: events for evidenced happenings, facts with source + quote. Auto-applied, audited, always yours to correct.')}
          onClick={() => void runReconstruct()}
        >
          <Sparkles className="icon-sm" /> {reconBusy ? t('Reconstructing…') : t('Reconstruct timeline')}
        </button>
      </div>

      {/* ── Stream filters ── */}
      <div className="flex items-center gap-8 mb-8 flex-wrap">
        <h4 className="section-label" style={{ margin: 0 }}>{t('Timeline')}</h4>
        <span className="ml-auto" />
        {activeEngagements.length > 1 && (
          <select className="form-select" style={{ width: 'auto' }} value={engFilter} onChange={(e) => setEngFilter(e.target.value)}>
            <option value="">{t('all engagements')}</option>
            {activeEngagements.map((e) => <option key={e.id} value={e.id}>{e.code}</option>)}
          </select>
        )}
        <input className="form-input mono" type="date" style={{ width: 140 }} title={t('From')} value={from} onChange={(e) => setFrom(e.target.value)} />
        <span className="text-xs text-ink-4">→</span>
        <input className="form-input mono" type="date" style={{ width: 140 }} title={t('To')} value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
      {(shaped.folders.length > 1 || shaped.kinds.length > 0) && (
        <div className="filter-chips mb-12" style={{ flexWrap: 'wrap' }}>
          <button className={`chip${!folderFilter && !kindFilter ? ' active' : ''}`} onClick={() => { setFolderFilter(''); setKindFilter(''); }}>{t('All')}</button>
          {shaped.folders.map((f) => (
            <button key={`f-${f}`} className={`chip${folderFilter === f ? ' active' : ''}`} title={t('Documents in this folder')}
              onClick={() => setFolderFilter(folderFilter === f ? '' : f)}>{t(f)}</button>
          ))}
          {shaped.kinds.map((k) => (
            <button key={`k-${k}`} className={`chip${kindFilter === k ? ' active' : ''}`} title={t('{kind} events', { kind: t(k) })}
              onClick={() => setKindFilter(kindFilter === k ? '' : k)}>◆ {t(k)}</button>
          ))}
        </div>
      )}

      {/* ── The stream ── */}
      {loading && <div className="flex justify-center p-24"><div className="spinner" /></div>}
      {!loading && !shaped.visible.length && (
        <div className="empty-state-card mb-24">
          <div className="empty-state-title">{stream?.count ? t('Nothing matches these filters') : t('Nothing on the timeline yet')}</div>
          <div className="empty-state-desc">
            {stream?.count
              ? t('Widen the date range or clear a chip.')
              : t('Every library document is automatically an entry here. Upload documents, or add an event by hand.')}
          </div>
        </div>
      )}
      {!loading && shaped.visible.length > 0 && (
        <div className="tl-stream mb-24">
          {shaped.visible.map((entry: TimelineEntry, i: number) => {
            const prevDate = i > 0 ? shaped.visible[i - 1].date : null;
            return entry.layer === 'document'
              ? renderDocRow(entry as DocumentEntry, prevDate)
              : renderEventRow(entry as EventEntry, prevDate);
          })}
        </div>
      )}

      {/* ── Add / edit event modal ── */}
      {eventDraft && (
        <div className="modal-overlay" onClick={() => setEventDraft(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{eventDraft.id ? t('Edit event') : t('Add event')}</h2>
              <button className="close-btn" onClick={() => setEventDraft(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="flex gap-12">
                <div className="form-group" style={{ width: 150 }}>
                  <label className="form-label">{t('Kind')}</label>
                  <select className="form-select" value={eventDraft.kind}
                    onChange={(e) => setEventDraft({ ...eventDraft, kind: e.target.value as EventKind })}>
                    {EVENT_KINDS.map((k) => <option key={k} value={k}>{t(k)}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ width: 150 }}>
                  <label className="form-label">{t('Date')}</label>
                  <input className="form-input mono" type="date" value={eventDraft.occurred_at}
                    onChange={(e) => setEventDraft({ ...eventDraft, occurred_at: e.target.value })} />
                </div>
                <div className="form-group" style={{ width: 110 }}>
                  <label className="form-label">{t('Time')}</label>
                  <input className="form-input mono" type="time" value={eventDraft.occurred_time}
                    onChange={(e) => setEventDraft({ ...eventDraft, occurred_time: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('Title')}</label>
                <input className="form-input" placeholder={t("What happened — 'NDA signed', 'Kickoff agreed for July'")}
                  value={eventDraft.title} onChange={(e) => setEventDraft({ ...eventDraft, title: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('Summary')}</label>
                <textarea className="form-textarea" rows={2} value={eventDraft.summary}
                  onChange={(e) => setEventDraft({ ...eventDraft, summary: e.target.value })} />
              </div>
              <div className="flex gap-12">
                <div className="form-group flex-1">
                  <label className="form-label">{t('Engagement')}</label>
                  <select className="form-select" value={eventDraft.engagement_id}
                    onChange={(e) => setEventDraft({ ...eventDraft, engagement_id: e.target.value })}>
                    <option value="">{t('— none (client-wide) —')}</option>
                    {detail.engagements.map((e) => <option key={e.id} value={e.id}>{e.code}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('Participants — one per line,')} <span className="mono">Name &lt;email&gt;</span></label>
                <textarea className="form-textarea" rows={2} placeholder={'Thomas Combemale <thomas@dec-group.ch>'}
                  value={eventDraft.participantsText}
                  onChange={(e) => setEventDraft({ ...eventDraft, participantsText: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('Source documents — the evidence this event anchors to')}</label>
                {!libraryDocs && <div className="text-xs text-ink-3">{t('Loading the library…')}</div>}
                {libraryDocs && !libraryDocs.length && <div className="text-xs text-ink-3">{t('The library is empty — the event will be recorded unanchored.')}</div>}
                {libraryDocs && libraryDocs.length > 0 && (
                  <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 'var(--r2)', padding: 'var(--s8)' }}>
                    {libraryDocs.map((d) => (
                      <label key={d.id} className="flex items-center gap-8 text-xs text-ink-2" style={{ padding: '2px 0', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={eventDraft.document_ids.includes(d.id)}
                          onChange={(e) => setEventDraft({
                            ...eventDraft,
                            document_ids: e.target.checked
                              ? [...eventDraft.document_ids, d.id]
                              : eventDraft.document_ids.filter((id) => id !== d.id),
                          })}
                        />
                        <span className="src-ref">{d.ref}</span>
                        <span className="truncate">{d.original_filename}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEventDraft(null)}>{t('Cancel')}</button>
              <button className="btn btn-accent" disabled={eventSaving} onClick={() => void saveEvent()}>
                {eventSaving ? t('Saving…') : eventDraft.id ? t('Save event') : t('Add event')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={t('Delete event')}
        confirmLabel={busy ? t('Deleting…') : t('Delete')}
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={() => void runConfirmModal()}
      >
        {confirm && (
          <p className="text-sm text-ink-2">
            {t('Delete “{label}”? The documents it anchored to stay in the library — only the event goes. The audit log keeps the record.', { label: confirm.label })}
          </p>
        )}
      </ConfirmModal>

      <DischargeModal
        open={!!discharging}
        commitment={discharging}
        documents={libraryDocs ?? []}
        events={(stream?.entries ?? []).filter((e): e is EventEntry => e.layer === 'event')}
        onClose={() => setDischarging(null)}
        onDischarged={() => { void loadStream(true); onCommitmentChanged?.(); }}
      />
    </>
  );
}
